import {
  formatRelativeDate,
  formatTime,
  IconStopOctagonMinus,
  isDateAfter,
  pluralize,
} from '@leland-dev/leland-ui-library';
import React, { type FC } from 'react';

import { CoachVisibility } from '../../__generated-gql-types__/globalTypes';

import { type CoachAvailabilityInfoCoachFragment } from './__generated-gql-types__/CoachAvailabilityInfo.generated';

interface CoachAvailabilityInfoProps {
  className?: string;
  coach: CoachAvailabilityInfoCoachFragment;
  overrideShowClosedNoMessage?: boolean;
}

export const CoachAvailabilityInfo: FC<CoachAvailabilityInfoProps> = ({
  className,
  coach,
  overrideShowClosedNoMessage,
}) => {
  const responseTime = coach?.averageFirstResponseLatency;
  const responseTimeBucket = responseTime
    ? responseTime > 24 && responseTime <= 48
      ? 48
      : Math.round(responseTime)
    : null;

  const showAvailabilityBox =
    coach.visibility === CoachVisibility.PRIVATE_CLOSED ||
    !!coach.availability?.nextAvailableAt ||
    overrideShowClosedNoMessage;
  const showDetail =
    !!coach.availability?.detail && !overrideShowClosedNoMessage;

  const noContactForNewClient =
    coach.visibility === CoachVisibility.PRIVATE_CLOSED &&
    !coach.coachingRelationship;

  return showAvailabilityBox || showDetail ? (
    <div className={`space-y-3 ${className}`}>
      {showAvailabilityBox ? (
        <div className="flex items-center space-x-2 rounded-lg border p-4">
          {noContactForNewClient || overrideShowClosedNoMessage ? (
            <>
              <IconStopOctagonMinus className="size-6 shrink-0" />
              <div className="ml-3 leading-none text-leland-gray-dark">
                <p className="text-lg font-medium leading-none">
                  Not currently taking new clients
                </p>
                {!overrideShowClosedNoMessage && !noContactForNewClient ? (
                  <p className="mt-2 text-leland-gray-light">
                    {coach.user.firstName} can still be messaged
                  </p>
                ) : null}
              </div>
            </>
          ) : coach.availability?.nextAvailableAt &&
            isDateAfter(coach.availability.nextAvailableAt, Date.now()) ? (
            <>
              <div className="grow text-left">
                <p className="font-medium">
                  Available{' '}
                  {formatRelativeDate(
                    coach.availability.nextAvailableAt,
                    Date.now(),
                  )}{' '}
                  at {formatTime(coach.availability.nextAvailableAt)}
                </p>
                {responseTimeBucket && responseTimeBucket <= 48 ? (
                  <p className="text-leland-gray-light">
                    Usually responds{' '}
                    {responseTimeBucket < 1
                      ? 'immediately'
                      : `within ${pluralize(responseTimeBucket, 'hour')}`}
                  </p>
                ) : null}
              </div>
              <div className="rounded-full bg-green-100 p-2">
                <div className="size-3 rounded-full bg-leland-primary" />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {showDetail && coach.availability?.detail ? (
        <p>{coach.availability.detail}</p>
      ) : null}
    </div>
  ) : null;
};
